/**
 * Accesos → Personas → one persona (Plan J, J3 — mockup 5, detail half).
 *
 * FOUR SECTIONS
 * -------------
 * **Identidad** (the `EditUserModal` fields, inline: name, role,
 * is_active; the e-mail is not editable and is rendered as text rather
 * than as a disabled input), **Grupos** (the memberships, add and
 * remove, each row linking to the group), **Ámbito efectivo**
 * (read-only, `EffectiveScopeCard`) and **Acceso al builder** (the six
 * `builder_user_access` flags).
 *
 * WHY BUILDER ACCESS IS PER PERSON AND NOT PER GROUP
 * --------------------------------------------------
 * `builder_user_access` is PervasiveMind staff access to the BUILDER;
 * groups govern what a tenant person sees in the PORTAL. They are
 * orthogonal, and a group that could carry builder flags would let a
 * tenant-facing object grant entry to the admin console.
 *
 * WHAT IS NOT HERE, AND WHY
 * -------------------------
 * * **The "solo entregas" switch.** This paragraph used to say that
 *   `deps.py` "reserves its ENFORCEMENT for J4", so a toggle here would
 *   be a control an analyst flips believing it locks somebody out. J4
 *   shipped the enforcement — the portal answers 401 to an interactive
 *   token for a `login_allowed=false` identity, refuses to mint one at
 *   `POST /api/auth/login`, and the platform console refuses the full
 *   crossing with 409 — so that reason is spent and is recorded here as
 *   spent rather than repeated.
 *
 *   The switch is still not on this screen, and the reason now is
 *   plainly smaller: nothing this page loads can tell it apart.
 *   `adminUsersApi.get` returns `UserAdminOut`, which does not carry
 *   `login_allowed` (only `GroupMemberOut` does), so a toggle would
 *   have to render a state it cannot read. The writer exists and it
 *   audits: `PATCH /api/builder/admin/users/{id}/login-allowed` →
 *   `user.login_allowed_change`, one row in `access_audit`, in the same
 *   transaction. Until this screen grows the control, the operator path
 *   is that endpoint, and `docs/operator-runbook/access-scopes.md`
 *   §16.2 is the recipe — including the check that the BUILDER's own
 *   login does not read the column, so a delivery-only identity holding
 *   `can_enter` still gets into this console.
 * * **"Ver como".** This said the backend could not perform the
 *   crossing — "`deps.py:167` still binds `set_current_scope(SCOPE_ALL)`
 *   unconditionally and there is no scope-override endpoint". Re-read
 *   against the tree, both halves were already false the day this file
 *   was written: `_bind_preview_scope` landed in `7e20ac56`, two
 *   commits before `14b5427a` added this page. `get_current_user` binds
 *   `SCOPE_ALL` only when NO override is asked for; when one is, that
 *   function authorises it, records `view_as.scope_preview` and binds
 *   it, on the six exactly-matched `/api/builder/preview/*` routes. The
 *   paragraph said "verified against the tree" and was not, which is
 *   the whole argument for re-reading a comment instead of trusting it.
 *
 *   The button is still absent and the real reason is a different one:
 *   both shapes of "Ver como" are asked for by an API caller today.
 *   BY SCOPE is the `X-Preview-Scope` header, which no screen in this
 *   frontend sends (grep it). BY PERSON is a portal token this app
 *   cannot mint at all — the platform console does, with `read_only`,
 *   and its own frontend does not send that flag either
 *   (`docs/operator-runbook/access-scopes.md` §16.3). So a button here
 *   is missing UI on both sides of a working backend, which is a
 *   smaller and more accurate claim than the one it replaces.
 * * **`subscription_count` / `override_count`** are shown once, as a
 *   legacy note with the sentence that says what they are, and not as
 *   headline numbers: they count rows in the tables this slice retires.
 */

import { Button, Input, Select, type SelectOption } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  FormField,
  PageContainer,
  PageHeader,
  Section,
  SectionStack,
  StatusChip,
  type Column,
  type RowAction,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { ArrowLeft, KeyRound, Save, Trash2, UserPlus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useT } from "@/i18n";
import {
  SCOPE_ALL,
  groupsApi,
  type GroupSummaryOut,
} from "@/lib/access-api";
import {
  ADMIN_USER_ROLES,
  adminUsersApi,
  type UserAdmin,
} from "@/lib/admin-users-api";
import { AccessTabs } from "@/pages/admin-access/AccessTabs";
import { BuilderAccessCard } from "@/pages/admin-access/BuilderAccessCard";
import {
  EffectiveScopeCard,
  type EffectiveScopeGroup,
} from "@/pages/admin-access/EffectiveScopeCard";
import { JoinGroupsModal } from "@/pages/admin-access/JoinGroupsModal";
import { ResetPasswordModal } from "@/pages/admin-access/ResetPasswordModal";

interface Membership extends EffectiveScopeGroup {
  /** The person's binding as THIS group's member row reports it. */
  effective_scope_id: string;
}

export function PersonDetailPage(): JSX.Element {
  const { userId = "" } = useParams<{ userId: string }>();
  const { t } = useT();

  const [user, setUser] = useState<UserAdmin | null>(null);
  const [allGroups, setAllGroups] = useState<GroupSummaryOut[]>([]);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [effectiveScopeId, setEffectiveScopeId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [joining, setJoining] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * The person's memberships, assembled from the groups router.
   *
   * Same composition as `listPeople` and for the same reason: there is
   * no `GET /api/builder/admin/people/{id}`. One call per group; the
   * cost is the group count, not the people count.
   */
  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [userRow, groupList] = await Promise.all([
        adminUsersApi.get(userId),
        groupsApi.list(),
      ]);
      setUser(userRow);
      setName(userRow.name);
      setRole(userRow.role);
      setIsActive(userRow.is_active);
      setAllGroups(groupList.groups);

      const mine: Membership[] = [];
      let bound: string | null = null;
      for (const g of groupList.groups) {
        const members = await groupsApi.allMembers(g.id);
        const hit = members.find((m) => m.user_id === userId);
        if (hit === undefined) continue;
        mine.push({
          id: g.id,
          name: g.name,
          scope_id: g.scope_id,
          scope_name: g.scope_name,
          effective_scope_id: hit.effective_scope_id,
        });
        bound = hit.effective_scope_id;
      }
      setMemberships(mine);
      setEffectiveScopeId(bound);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const saveIdentity = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await adminUsersApi.update(userId, {
        name: name.trim(),
        role,
        is_active: isActive,
      });
      // Re-read everything: a role change moves the builder flags (an
      // admin reads all-true) and the backend recomputes the effective
      // scope, and neither travels in the PUT's response.
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const leaveGroup = async (membership: Membership): Promise<void> => {
    setError(null);
    try {
      const out = await groupsApi.removeMember(membership.id, userId);
      setNotice(out.note);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const groupColumns: Column<Membership>[] = [
    {
      id: "group",
      header: t("access.people.groups.col.group"),
      cell: (row) => (
        <Link
          to={`/admin/access/groups/${encodeURIComponent(row.id)}`}
          className="font-medium text-primary-ink underline underline-offset-2"
        >
          {row.name}
        </Link>
      ),
    },
    {
      id: "scope",
      header: t("access.people.groups.col.scope"),
      width: "w-56",
      cell: (row) => (
        <StatusChip
          label={row.scope_id === SCOPE_ALL ? t("access.scopes.all") : row.scope_name}
          tone={row.scope_id === SCOPE_ALL ? "warning" : "info"}
          title={row.scope_id}
        />
      ),
    },
  ];

  const groupActions: RowAction<Membership>[] = [
    {
      id: "leave",
      label: t("access.people.groups.leave"),
      icon: <Trash2 size={14} />,
      danger: true,
      onSelect: (row) => void leaveGroup(row),
    },
  ];

  const roleOptions: SelectOption[] = ADMIN_USER_ROLES.map((r) => ({
    value: r,
    label: r,
  }));

  return (
    <PageContainer wide>
      <PageHeader
        eyebrow={
          <Link
            to="/admin/access/people"
            className="inline-flex items-center gap-1 text-[13px] font-medium text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft size={13} />
            {t("access.people.title")}
          </Link>
        }
        title={user?.name ?? userId}
        subtitle={user?.email}
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<KeyRound size={14} />}
            onClick={() => setResetting(true)}
          >
            {t("access.people.actions.reset")}
          </Button>
        }
      />
      <AccessTabs active="people" />

      {error !== null && (
        <Callout tone="danger" title={t("access.common.errorTitle")}>
          <span className="whitespace-pre-wrap">{error}</span>
        </Callout>
      )}
      {notice !== null && (
        <Callout tone="info" title={t("access.groups.members.noticeTitle")}>
          <span className="whitespace-pre-wrap">{notice}</span>
        </Callout>
      )}

      {loading || user === null ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-neutral-500">
          <Spinner size={16} ariaLabel={t("common.loading")} />
          {t("common.loading")}
        </div>
      ) : (
        <SectionStack>
          <Section
            title={t("access.people.identity")}
            description={t("access.people.identityDesc")}
            actions={
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Save size={14} />}
                disabled={saving}
                onClick={() => void saveIdentity()}
              >
                {t("common.save")}
              </Button>
            }
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label={t("access.people.form.name")} required>
                <Input
                  value={name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setName(e.target.value)
                  }
                />
              </FormField>

              {/* Text, not a disabled input: the e-mail is the identity
                  and cannot be changed here at all, so a control would
                  say something false. */}
              <div>
                <p className="text-[13px] font-semibold text-neutral-600">
                  {t("access.people.form.email")}
                </p>
                <p className="font-mono text-sm text-neutral-900">
                  {user.email}
                </p>
                <p className="text-[13px] text-neutral-500">
                  {t("access.people.form.emailFixed")}
                </p>
              </div>

              <FormField
                label={t("access.people.form.role")}
                help={t("access.people.form.roleHelp")}
              >
                <Select
                  aria-label={t("access.people.form.role")}
                  value={role}
                  options={roleOptions}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setRole(e.target.value)
                  }
                />
              </FormField>

              <FormField label={t("access.people.form.status")}>
                <Select
                  aria-label={t("access.people.form.status")}
                  value={isActive ? "active" : "inactive"}
                  options={[
                    { value: "active", label: t("access.people.active") },
                    { value: "inactive", label: t("access.people.inactive") },
                  ]}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setIsActive(e.target.value === "active")
                  }
                />
              </FormField>
            </div>

            <p className="mt-3 text-[13px] text-neutral-600">
              {t("access.people.legacyCounts", {
                subs: user.subscription_count,
                overrides: user.override_count,
              })}
            </p>
          </Section>

          <Section
            title={t("access.people.groups.title")}
            description={t("access.people.groups.desc")}
            actions={
              <Button
                variant="primary"
                size="sm"
                leftIcon={<UserPlus size={14} />}
                onClick={() => setJoining(true)}
              >
                {t("access.people.groups.join")}
              </Button>
            }
            flush
          >
            <DataTable
              columns={groupColumns}
              rows={memberships}
              rowKey={(row) => row.id}
              actions={groupActions}
              caption={t("access.people.groups.title")}
              empty={
                <Callout
                  tone="warning"
                  title={t("access.people.noGroups")}
                  className="m-4"
                >
                  {t("access.people.noGroupsHint")}
                </Callout>
              }
            />
          </Section>

          <Section title={t("access.people.effective.title")}>
            <EffectiveScopeCard
              effectiveScopeId={effectiveScopeId}
              groups={memberships}
              editGroupsHref={`/admin/access/people/${encodeURIComponent(userId)}`}
              t={t}
            />
          </Section>

          <Section
            title={t("access.people.builder.title")}
            description={t("access.people.builder.desc")}
          >
            <BuilderAccessCard user={user} onError={setError} />
          </Section>
        </SectionStack>
      )}

      <JoinGroupsModal
        open={joining}
        userId={userId}
        allGroups={allGroups}
        current={memberships.map((m) => m.id)}
        onClose={() => setJoining(false)}
        onJoined={(note) => {
          setJoining(false);
          setNotice(note);
          void refresh();
        }}
        onChanged={(note) => {
          setNotice(note);
          void refresh();
        }}
      />

      <ResetPasswordModal
        user={resetting ? user : null}
        onClose={() => setResetting(false)}
      />
    </PageContainer>
  );
}
